import React, { useEffect, useRef, useState } from "react";
import { X, Loader, CheckCircle, AlertCircle, Pause, Play, Zap, ChevronRight } from "lucide-react";
import { getMissingFeeItems, backfillSfFeesPage, backfillOneWfsFee } from "../../services/catalogService";
import { getWalmartFeeSettings } from "../../utils/walmartFees";
import WalmartFeeSettingsModal from "../walmart/WalmartFeeSettingsModal";

const toApiSettings = (s) => ({
    sales_tax: s.salesTax || 0,
    inbound_shipping: s.inboundShipping || 0,
    fulfillment_cost: s.fulfillmentCost || 0,
});

const randomDelay = () => 500 + Math.random() * 1000; // 500-1500ms between WFS lookups

const emptyFilters = () => ({
    marketplace: "", availability: "", publishedStatus: "PUBLISHED",
    sf_stock_min: "", sf_stock_max: "", wfs_stock_min: "", wfs_stock_max: "",
});

const StepHeader = ({ n, label, done }) => (
    <div className="flex items-center gap-2 mb-3">
        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-extrabold flex-shrink-0 ${done ? "bg-green-500 text-white" : "bg-teal-600 text-white"}`}>
            {done ? <CheckCircle size={14} /> : n}
        </span>
        <span className="text-sm font-extrabold text-gray-800 uppercase tracking-wide">{label}</span>
    </div>
);

const BackfillFeesModal = ({ filters: initialFilters = {}, onClose, onDone }) => {
    // ══════════════════════════════════════════════
    // STEP 1 — scope
    // ══════════════════════════════════════════════
    const [filters, setFilters] = useState(() => ({ ...emptyFilters(), ...initialFilters }));
    const [includeExisting, setIncludeExisting] = useState(false);
    const [missing, setMissing] = useState(null); // { sf_count, sf_items, wfs_items }
    const [scanError, setScanError] = useState("");
    const [scanning, setScanning] = useState(false);
    const [scoped, setScoped] = useState(false); // true once the seller confirms scope and moves to step 2

    const setFilter = (key, val) => setFilters((f) => ({ ...f, [key]: val }));
    const resetFiltersToCurrentView = () => setFilters({ ...emptyFilters(), ...initialFilters });

    const runScan = () => {
        setScanError("");
        setScanning(true);
        getMissingFeeItems(filters, { includeExisting })
            .then((d) => { setMissing(d); setScoped(true); })
            .catch((e) => {
                console.error("[BackfillFeesModal] scan failed:", e);
                setScanError(e.message || "Failed to scan your catalog — check your connection and try again.");
            })
            .finally(() => setScanning(false));
    };

    // ══════════════════════════════════════════════
    // STEP 2 — fee settings
    // ══════════════════════════════════════════════
    const [currentSettings, setCurrentSettings] = useState(() => getWalmartFeeSettings());
    const [showFeeSettings, setShowFeeSettings] = useState(false);
    const [settingsConfirmed, setSettingsConfirmed] = useState(false);
    const settingsAllZero = !currentSettings.salesTax && !currentSettings.inboundShipping && !currentSettings.fulfillmentCost;

    // ══════════════════════════════════════════════
    // STEP 3 — run
    // ══════════════════════════════════════════════
    const [sfDone, setSfDone] = useState(false);
    const [sfProcessing, setSfProcessing] = useState(false);
    const [sfProcessedCount, setSfProcessedCount] = useState(0);
    const [sfTotal, setSfTotal] = useState(null);
    const [sfError, setSfError] = useState("");

    const [wfsProcessed, setWfsProcessed] = useState(0);
    const [wfsSkipped, setWfsSkipped] = useState(0);
    const [wfsFailed, setWfsFailed] = useState(0);
    const [wfsRunning, setWfsRunning] = useState(false);
    const [paused, setPaused] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [wfsDebugLog, setWfsDebugLog] = useState([]);
    const [wfsError, setWfsError] = useState("");
    const pausedRef = useRef(false);
    const cancelledRef = useRef(false);

    useEffect(() => () => { cancelledRef.current = true; }, []);

    const runSf = async () => {
        setSfError("");
        setSfProcessing(true);
        try {
            const settings = toApiSettings(currentSettings);
            let done = false;
            let offset = 0;
            let totalSoFar = 0;
            let firstCallTotal = null;
            let guard = 0; // hard safety cap so a backend bug can never loop forever client-side
            while (!done) {
                if (cancelledRef.current) return;
                guard += 1;
                if (guard > 500) {
                    throw new Error("Stopped after 500 pages — this doesn't look like it's making progress. Please retry.");
                }
                const res = await backfillSfFeesPage(settings, filters, offset, 200, { includeExisting });
                totalSoFar += res.processed;
                setSfProcessedCount(totalSoFar);
                if (firstCallTotal === null) {
                    firstCallTotal = res.total;
                    setSfTotal(res.total);
                }
                offset = res.next_offset;
                done = res.done || res.processed === 0;
            }
            setSfDone(true);
        } catch (e) {
            console.error("[BackfillFeesModal] SF backfill failed:", e);
            setSfError(e.message || "SF backfill failed — check your connection and try again.");
        } finally {
            setSfProcessing(false);
        }
    };

    const runWfs = async () => {
        setWfsError("");
        setWfsRunning(true);
        const settings = toApiSettings(currentSettings);
        let consecutiveFailures = 0;
        for (const item of missing.wfs_items) {
            if (cancelledRef.current) return;
            while (pausedRef.current) {
                await new Promise((r) => setTimeout(r, 300));
                if (cancelledRef.current) return;
            }
            setCurrentItem(item);
            try {
                const res = await backfillOneWfsFee(item.id, settings, { force: includeExisting });
                consecutiveFailures = 0;
                if (res.skipped) {
                    setWfsSkipped((s) => s + 1);
                } else {
                    setWfsProcessed((p) => p + 1);
                    setWfsDebugLog((log) => [...log, { item, ...res.debug, total_fees: res.total_fees }]);
                }
            } catch (e) {
                console.error(`[BackfillFeesModal] WFS backfill failed for ${item.sku}:`, e);
                setWfsFailed((f) => f + 1);
                consecutiveFailures += 1;
                // 5 failures in a row almost always means something systemic
                // (auth expired, network down) rather than 5 unrelated bad
                // items — stop and surface it instead of silently burning
                // through the rest of the list with the same failure.
                if (consecutiveFailures >= 5) {
                    setWfsError(`Stopped after 5 failures in a row (last error: "${e.message || "unknown error"}"). Check your connection/login, then Retry.`);
                    break;
                }
            }
            await new Promise((r) => setTimeout(r, randomDelay()));
        }
        setWfsRunning(false);
        setCurrentItem(null);
        onDone && onDone();
    };

    const togglePause = () => {
        pausedRef.current = !pausedRef.current;
        setPaused(pausedRef.current);
    };

    const handleClose = () => {
        cancelledRef.current = true;
        onClose();
    };

    const sfItemsCount = missing?.sf_count ?? 0;
    const wfsTotal = missing?.wfs_items?.length || 0;
    const wfsDoneCount = wfsProcessed + wfsSkipped + wfsFailed;
    const wfsComplete = wfsTotal > 0 && wfsDoneCount >= wfsTotal;
    const nothingToDo = missing && missing.sf_count === 0 && wfsTotal === 0;
    const anyRunStarted = sfProcessing || sfDone || wfsRunning || wfsDoneCount > 0;

    return (
        <div className="fixed inset-0 bg-black/60 z-[999999] flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden my-8">
                <div className="bg-gradient-to-r from-teal-500 to-blue-600 px-6 py-4 flex items-center justify-between">
                    <h2 className="text-white font-bold text-lg">Backfill Fees</h2>
                    <button onClick={handleClose} className="text-white/70 hover:text-white"><X size={20} /></button>
                </div>
                <div className="p-6 max-h-[80vh] overflow-y-auto">

                    {/* ═══════════════ STEP 1 — SCOPE ═══════════════ */}
                    <div className={`border-2 rounded-xl p-4 mb-4 ${scoped ? "border-gray-200" : "border-teal-300"}`}>
                        <StepHeader n={1} label="Choose what to run this on" done={scoped} />

                        <div className={scoped ? "opacity-50 pointer-events-none" : ""}>
                            <p className="text-[11px] text-gray-400 mb-3">
                                Defaults to your current Catalog view. Adjust anything below to widen or narrow scope.
                            </p>
                            <div className="grid grid-cols-2 gap-2 mb-3">
                                <select value={filters.availability}
                                    onChange={(e) => setFilter("availability", e.target.value)}
                                    className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500 bg-white">
                                    <option value="">Any availability</option>
                                    <option value="IN_STOCK">In Stock</option>
                                    <option value="OUT_OF_STOCK">Out of Stock</option>
                                </select>
                                <select value={filters.publishedStatus}
                                    onChange={(e) => setFilter("publishedStatus", e.target.value)}
                                    className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500 bg-white">
                                    <option value="">Any publish status</option>
                                    <option value="PUBLISHED">Published</option>
                                    <option value="UNPUBLISHED">Unpublished</option>
                                </select>
                                <input type="number" placeholder="SF stock min" value={filters.sf_stock_min}
                                    onChange={(e) => setFilter("sf_stock_min", e.target.value)}
                                    className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                                <input type="number" placeholder="WFS stock min" value={filters.wfs_stock_min}
                                    onChange={(e) => setFilter("wfs_stock_min", e.target.value)}
                                    className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                            </div>

                            <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer mt-3 pt-3 border-t border-gray-100">
                                <input type="checkbox" checked={includeExisting}
                                    onChange={(e) => setIncludeExisting(e.target.checked)} className="accent-teal-600" />
                                Also update items that already have a fee estimate (re-runs and overwrites, not just items missing one)
                            </label>
                        </div>

                        {scanError && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 mt-3 text-xs text-red-700 flex items-start gap-1.5">
                                <AlertCircle size={13} className="flex-shrink-0 mt-0.5" /> {scanError}
                            </div>
                        )}

                        {!scoped ? (
                            <button type="button" onClick={runScan} disabled={scanning}
                                className="w-full mt-3 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-bold transition disabled:opacity-50 flex items-center justify-center gap-1.5">
                                {scanning ? <><Loader size={14} className="animate-spin" /> Scanning...</> : scanError ? "Retry Scan" : <>Scan Catalog <ChevronRight size={14} /></>}
                            </button>
                        ) : (
                            <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                                <p className="text-xs text-gray-500">
                                    Found <span className="font-bold text-gray-700">{sfItemsCount}</span> SF item(s) and{" "}
                                    <span className="font-bold text-gray-700">{wfsTotal}</span> WFS item(s) in scope.
                                </p>
                                {!anyRunStarted && (
                                    <button type="button" onClick={() => { setScoped(false); setMissing(null); }} className="text-[11px] text-teal-600 hover:text-teal-700 font-semibold flex-shrink-0">
                                        Edit Scope
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {scoped && nothingToDo && (
                        <div className="text-center py-8">
                            <CheckCircle size={32} className="text-green-500 mx-auto mb-2" />
                            <p className="text-sm text-gray-600">Nothing matches this scope. Try adjusting the filters above.</p>
                        </div>
                    )}

                    {scoped && !nothingToDo && (
                        <>
                            {/* ═══════════════ STEP 2 — FEE SETTINGS ═══════════════ */}
                            <div className={`border-2 rounded-xl p-4 mb-4 ${settingsConfirmed ? "border-gray-200" : "border-teal-300 bg-teal-50"}`}>
                                <StepHeader n={2} label="Fee settings" done={settingsConfirmed} />

                                <div className="flex items-center justify-between mb-3">
                                    <span className="text-xs font-bold text-gray-500 uppercase">Used for this run</span>
                                    <button type="button" onClick={() => setShowFeeSettings(true)}
                                        disabled={anyRunStarted}
                                        className="text-xs bg-white border-2 border-teal-400 text-teal-700 font-bold px-3 py-1.5 rounded-lg hover:bg-teal-100 transition disabled:opacity-40">
                                        Edit Settings
                                    </button>
                                </div>
                                <div className="grid grid-cols-3 gap-3 text-center mb-2">
                                    <div className="bg-white rounded-lg py-2 border border-teal-100">
                                        <p className="text-[10px] text-gray-400 uppercase font-bold">Sales Tax</p>
                                        <p className="font-extrabold text-lg text-gray-800">{currentSettings.salesTax || 0}%</p>
                                    </div>
                                    <div className="bg-white rounded-lg py-2 border border-teal-100">
                                        <p className="text-[10px] text-gray-400 uppercase font-bold">Inbound $/lb</p>
                                        <p className="font-extrabold text-lg text-gray-800">${currentSettings.inboundShipping || 0}</p>
                                    </div>
                                    <div className="bg-white rounded-lg py-2 border border-teal-100">
                                        <p className="text-[10px] text-gray-400 uppercase font-bold">Fulfillment $</p>
                                        <p className="font-extrabold text-lg text-gray-800">${currentSettings.fulfillmentCost || 0}</p>
                                    </div>
                                </div>
                                {settingsAllZero && (
                                    <p className="text-xs text-orange-600 font-semibold mb-2 flex items-center gap-1">
                                        <AlertCircle size={13} /> These all look unset (0) — click Edit Settings if that's not intentional.
                                    </p>
                                )}

                                <p className="text-[11px] text-gray-500 mt-3 pt-3 border-t border-gray-100 flex items-start gap-1.5">
                                    <CheckCircle size={13} className="text-green-500 flex-shrink-0 mt-0.5" />
                                    Items that already have a real Walmart-charged SF label cost or WFS inbound cost (from an actual
                                    shipment made through this tool) keep that real number.
                                </p>

                                {!settingsConfirmed ? (
                                    <button type="button" onClick={() => setSettingsConfirmed(true)}
                                        className="w-full mt-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-bold transition flex items-center justify-center gap-1.5">
                                        Confirm Settings <ChevronRight size={14} />
                                    </button>
                                ) : !anyRunStarted && (
                                    <button type="button" onClick={() => setSettingsConfirmed(false)} className="text-[11px] text-teal-600 hover:text-teal-700 font-semibold mt-3">
                                        Edit Settings
                                    </button>
                                )}
                            </div>

                            {/* ═══════════════ STEP 3 — RUN ═══════════════ */}
                            {settingsConfirmed && (
                                <div className="border-2 border-teal-300 rounded-xl p-4">
                                    <StepHeader n={3} label="Run it" />

                                    {sfItemsCount > 0 && (
                                        <div className="mb-5 pb-5 border-b border-gray-100">
                                            <p className="text-sm font-bold text-gray-700 mb-1 flex items-center gap-1.5">
                                                <Zap size={14} className="text-teal-500" /> SF Items — {sfItemsCount}
                                            </p>
                                            {sfTotal !== null && sfTotal !== sfItemsCount && (
                                                <p className="text-[11px] text-orange-500 mb-1">
                                                    Note: {sfTotal} needed processing when you clicked Process.
                                                </p>
                                            )}
                                            <p className="text-xs text-gray-500 mb-3">
                                                These update instantly since everything needed is already on file.
                                            </p>

                                            {(sfProcessing || sfDone) && sfTotal !== null && (
                                                <div className="mb-3">
                                                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                                                        <span>{sfProcessedCount} / {sfTotal}</span>
                                                        {sfDone && <span className="text-green-600 font-semibold">Complete</span>}
                                                    </div>
                                                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                                                        <div className="h-full bg-teal-500 transition-all" style={{ width: `${sfTotal > 0 ? (sfProcessedCount / sfTotal) * 100 : 100}%` }} />
                                                    </div>
                                                </div>
                                            )}

                                            {sfError && (
                                                <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 mb-2 text-xs text-red-700 flex items-start gap-1.5">
                                                    <AlertCircle size={13} className="flex-shrink-0 mt-0.5" /> {sfError}
                                                </div>
                                            )}

                                            {!sfDone ? (
                                                <button type="button" onClick={runSf} disabled={sfProcessing}
                                                    className="w-full py-2.5 bg-teal-600 text-white rounded-xl font-bold text-sm hover:bg-teal-700 transition disabled:opacity-50">
                                                    {sfProcessing ? <span className="flex items-center justify-center gap-1.5"><Loader size={14} className="animate-spin" /> Processing...</span>
                                                        : sfError ? "Retry" : `Process ${sfItemsCount} SF Item${sfItemsCount === 1 ? "" : "s"} Now`}
                                                </button>
                                            ) : (
                                                <p className="text-sm text-green-600 flex items-center gap-1.5">
                                                    <CheckCircle size={14} /> {sfProcessedCount} SF items updated
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    {wfsTotal > 0 && (
                                        <div>
                                            <p className="text-sm font-bold text-gray-700 mb-1">WFS Items — {wfsTotal}</p>
                                            <p className="text-xs text-gray-500 mb-3">
                                                These take longer to process — please leave this open until it finishes.
                                            </p>

                                            {(wfsRunning || wfsDoneCount > 0) && (
                                                <div className="mb-3">
                                                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                                                        <span>{wfsDoneCount} / {wfsTotal}</span>
                                                        <span>{wfsProcessed} updated · {wfsSkipped} skipped · {wfsFailed} failed</span>
                                                    </div>
                                                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                                                        <div className="h-full bg-blue-500 transition-all" style={{ width: `${(wfsDoneCount / wfsTotal) * 100}%` }} />
                                                    </div>
                                                    {currentItem && wfsRunning && (
                                                        <p className="text-[11px] text-gray-400 mt-1.5 truncate">
                                                            Processing: {currentItem.title || currentItem.sku}
                                                        </p>
                                                    )}
                                                </div>
                                            )}

                                            {wfsError && (
                                                <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 mb-2 text-xs text-red-700 flex items-start gap-1.5">
                                                    <AlertCircle size={13} className="flex-shrink-0 mt-0.5" /> {wfsError}
                                                </div>
                                            )}

                                            {!wfsRunning && wfsDoneCount === 0 && (
                                                <button type="button" onClick={runWfs}
                                                    className="w-full py-2.5 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-blue-700 transition">
                                                    Start WFS Backfill
                                                </button>
                                            )}
                                            {wfsRunning && (
                                                <button type="button" onClick={togglePause}
                                                    className="w-full py-2.5 bg-gray-100 text-gray-700 rounded-xl font-bold text-sm hover:bg-gray-200 transition flex items-center justify-center gap-2">
                                                    {paused ? <><Play size={14} /> Resume</> : <><Pause size={14} /> Pause</>}
                                                </button>
                                            )}
                                            {!wfsRunning && wfsError && (
                                                <button type="button" onClick={runWfs}
                                                    className="w-full mt-2 py-2.5 bg-blue-50 text-blue-700 border-2 border-blue-200 rounded-xl font-bold text-sm hover:bg-blue-100 transition">
                                                    Retry Remaining Items
                                                </button>
                                            )}
                                            {wfsFailed > 0 && !wfsError && (
                                                <p className="text-[11px] text-orange-500 mt-2 flex items-center gap-1">
                                                    <AlertCircle size={12} /> {wfsFailed} item(s) failed — re-open this modal to retry just those.
                                                </p>
                                            )}

                                            {wfsComplete && wfsDebugLog.length > 0 && (
                                                <div className="mt-3">
                                                    <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">What was found per item</p>
                                                    <div className="max-h-40 overflow-y-auto border border-gray-100 rounded-lg">
                                                        <table className="w-full text-[10px]">
                                                            <thead className="bg-gray-50 sticky top-0">
                                                                <tr className="text-left text-gray-400">
                                                                    <th className="px-2 py-1">Item</th>
                                                                    <th className="px-2 py-1">Weight</th>
                                                                    <th className="px-2 py-1">Dims</th>
                                                                    <th className="px-2 py-1">Cost Price</th>
                                                                    <th className="px-2 py-1">Real Inbound</th>
                                                                    <th className="px-2 py-1">Total Fees</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {wfsDebugLog.map((d, i) => (
                                                                    <tr key={i} className="border-t border-gray-50">
                                                                        <td className="px-2 py-1 truncate max-w-[100px]" title={d.item.title}>{d.item.sku}</td>
                                                                        <td className={`px-2 py-1 ${d.weight_found ? "text-green-600" : "text-red-500"}`}>{d.weight_found ? "✓" : "✗"}</td>
                                                                        <td className={`px-2 py-1 ${d.dimensions_found ? "text-green-600" : "text-red-500"}`}>{d.dimensions_found ? "✓" : "✗"}</td>
                                                                        <td className={`px-2 py-1 ${d.cost_price_found ? "text-green-600" : "text-orange-500"}`}>{d.cost_price_found ? `$${d.cost_price_used}` : "none"}</td>
                                                                        <td className={`px-2 py-1 ${d.preserved_real_inbound_cost ? "text-green-600 font-semibold" : "text-gray-300"}`}>{d.preserved_real_inbound_cost ? "kept" : "—"}</td>
                                                                        <td className="px-2 py-1 font-semibold">${d.total_fees.toFixed(2)}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {(sfDone || sfItemsCount === 0) && !wfsError && (wfsComplete || wfsTotal === 0) && (
                                        <div className="text-center pt-5 mt-5 border-t border-gray-100">
                                            <button type="button" onClick={handleClose} className="px-6 py-2 bg-gray-800 text-white rounded-xl font-bold hover:bg-gray-700 transition">
                                                Done
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {showFeeSettings && (
                <WalmartFeeSettingsModal onClose={() => {
                    setShowFeeSettings(false);
                    setCurrentSettings(getWalmartFeeSettings());
                }} />
            )}
        </div>
    );
};

export default BackfillFeesModal;